import { formatClock, type WeekMessageReply } from '@hotlist/shared';

/**
 * The app a civilian opens to find out what the network has on them.
 *
 * DESIGN.md section 4 aims the whole telemetry design at this screen, and its
 * shape is the argument. Every line is a figure a transparency portal publishes
 * in aggregate, about everybody, in a form nobody can find themselves in. Here
 * they are about **one plate**, which is the thing no portal does.
 *
 * The last line is always zero and is not a joke.
 *
 * ## It is drawn as an app
 *
 * It began as a modal over the map, which made it a report the game handed you
 * rather than a thing you went and checked, so it moved onto the phone next to
 * the work board and the car. But it went there as a heading with a `check`
 * button beside it and a page that swapped in underneath, which is not what an
 * app is -- it read as one more console screen, and that is precisely the wrong
 * voice: **the console belongs to the people doing the looking, and this
 * belongs to the person being looked at.**
 *
 * So it has its own chrome. A bar with a wordmark and a refresh, a card with
 * the plate on it, results under that, and a line saying when it last asked.
 * Before the first check it shows an empty state and a button, because an app
 * that has not been opened yet has nothing to say -- the figures arrive because
 * somebody went and looked, which is the only way anybody gets them.
 *
 * Nothing here is presented as an outrage and nothing is coloured red. It is a
 * table, in the same typeface as the console that produced the numbers, and it
 * says what happened. A player who has spent an hour driving to make rent
 * reading their own week back is the whole of the design; putting a verdict on
 * it would be the game doing the noticing again.
 */
export class YourWeekPanel {
  private readonly screen: HTMLElement;
  private readonly refresh: HTMLButtonElement;
  private readonly onOpen: () => void;
  private daySeconds = 24 * 60 * 60;
  /** Sim time of the last answer, so the app can say when it asked. */
  private checkedAt: number | null = null;

  constructor(onOpen: () => void) {
    this.onOpen = onOpen;
    this.screen = must('yourweek-body');
    this.refresh = must('flocked-refresh') as HTMLButtonElement;
    this.refresh.addEventListener('click', () => this.ask());
    this.idle();
  }

  setDaySeconds(seconds: number): void {
    if (seconds > 0) this.daySeconds = seconds;
  }

  /** Nothing asked yet: an app that has not been opened has nothing to report. */
  private idle(): void {
    this.screen.replaceChildren();
    const empty = document.createElement('div');
    empty.className = 'app-empty';

    const said = document.createElement('p');
    said.textContent =
      'Check what the plate reader network holds on your plate. Everything here is a figure the portals publish about everybody at once.';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'app-check';
    button.textContent = 'check my plate';
    button.addEventListener('click', () => this.ask());

    empty.append(said, button);
    this.screen.append(empty);
    // Nothing to refresh until there is something on the screen. The phone's
    // bar reads this rather than assuming, because it puts the app's controls
    // up and only this screen knows whether they mean anything yet.
    this.refresh.dataset.ready = '0';
    this.refresh.hidden = true;
  }

  private ask(): void {
    this.refresh.disabled = true;
    this.onOpen();
  }

  show(week: WeekMessageReply, time: number): void {
    this.checkedAt = time;
    this.refresh.dataset.ready = '1';
    this.refresh.hidden = false;
    this.refresh.disabled = false;
    this.screen.replaceChildren();

    const card = document.createElement('div');
    card.className = 'week-plate';
    const serial = document.createElement('span');
    serial.className = 'week-plate-serial';
    serial.textContent = week.plate;
    card.append(serial);
    this.screen.append(card);

    const window = document.createElement('div');
    window.className = 'week-window';
    window.textContent =
      week.windowDays > 0
        ? `the last ${week.windowDays} day${week.windowDays === 1 ? '' : 's'} of record`
        : 'the record so far';
    this.screen.append(window);

    for (const [label, value] of [
      ['plate reads of your vehicle', week.reads],
      ['cameras that saw it', week.cameras],
      ['days it was seen at all', week.daysSeen],
      ['most reads in one day', week.busiestDay],
      ['searches naming you', week.searches],
      ['...of those, with no reason given', week.searchesUnexplained],
      ['hotlist alerts on your plate', week.alerts],
      ['stops', week.stops],
      ['stops that were wrong', week.wrongfulStops],
      // The line no portal has, and the reason the rest are worth printing.
      ['stops reported by any portal', 0],
    ] as const) {
      const row = document.createElement('div');
      row.className = 'week-row';
      const name = document.createElement('span');
      name.textContent = label;
      const count = document.createElement('span');
      count.className = 'week-count';
      count.textContent = value.toLocaleString();
      row.append(name, count);
      this.screen.append(row);
    }

    const checked = document.createElement('div');
    checked.className = 'app-checked';
    checked.textContent = `checked ${formatClock(this.checkedAt, this.daySeconds)}`;
    this.screen.append(checked);
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
